import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { tap } from 'rxjs/operators'
import { action, computed, observable } from 'mobx'
import type { ProgressionStore } from '@/features/jobs/taxes/progression/ProgressionStore'
import { task } from 'mobx-task'
import { required, validationContext } from 'validx'
import type { SnackbarErrorHandlerFactory } from '@/features/shell'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type { GetProgressionFn } from './ProgressionViewModel'
import { toStream } from '@/utils/mobx-util'
import type { DocumentsListViewModelFactory } from '@/features/docs'

/**
 * Sends a message to the job's conversation.
 */
export type SendMessageFn = (message: string) => Promise<void>

/**
 * View model for draft reviewing.
 */
export class DraftReviewViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Modal for displaying draft approval.
   */
  readonly modal = new ModalViewModel()

  /**
   * Modal for displaying draft approval confirmation.
   */
  readonly confirmDraftApprovalModalVm = new ModalViewModel()

  /**
   * Modal for displaying draft rejection confirmation.
   */
  readonly confirmDraftRejectionModalVm = new ModalViewModel()

  /**
   * Documents list view model used to fetch draft returns.
   */
  readonly documentsListViewModel

  /**
   * The changes the client is requesting to the draft.
   */
  @observable accessor requestedChanges = ''

  readonly validation = validationContext(this, {
    requestedChanges: [required('Please describe the changes you need')],
  })

  constructor(
    public readonly job: SubmittedJob,
    private readonly _getProgression: GetProgressionFn,
    createDocumentsListViewModel: DocumentsListViewModelFactory,
    private readonly _progressionStore: ProgressionStore,
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
    private readonly _sendMessage: SendMessageFn,
  ) {
    this.documentsListViewModel = createDocumentsListViewModel({
      fetchAuthor: true,
      types: ['DRAFT_RETURN'],
      context: {
        type: 'Job',
        jobId: this.job.id,
      },
    })

    this._lifecycle.manage(this.documentsListViewModel)

    this._lifecycle.manage(
      toStream(() => this._getProgression()?.current, true).pipe(
        tap((s) => {
          if (s === 'DraftInReview') {
            this.reviewDraft()
          } else {
            this._closeDraftApprovalModals()
          }
        }),
      ),
    )
  }

  /**
   * The time the job most recently transitioned to `DraftRejected`, or null
   * if it has never been rejected.
   */
  @computed
  get mostRecentDraftRejectedAt(): Date | null {
    const items = this._getProgression()?.recordedSteps.items
    return (
      items?.filter((step) => step.step === 'DraftRejected').at(-1)
        ?.createdAt ?? null
    )
  }

  /**
   * The draft returns to review: all draft return documents uploaded after the
   * most recent rejection, or all of them if the job has never been rejected.
   */
  @computed
  get draftReturnVms() {
    const rejectedAt = this.mostRecentDraftRejectedAt
    return this.documentsListViewModel.allDocuments.filter(
      (d) =>
        d.doc.typeId === 'DRAFT_RETURN' &&
        (!rejectedAt || d.doc.dateCreated > rejectedAt),
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._closeDraftApprovalModals()
    this._lifecycle.deactivate()
  }

  /**
   * Shows the review dialog.
   */
  @action.bound
  reviewDraft() {
    void this.fetchDraftReturn(this._lifecycle.signal)
    this.modal.show()
  }

  /**
   * Fetches the draft return.
   */
  fetchDraftReturn = task(async (signal?: AbortSignal) => {
    await this.documentsListViewModel.fetch(signal)
  })

  /**
   * Approves the draft return.
   */
  approveDraft = task.resolved(async () => {
    await this._progressionStore
      .transitionToStep(this.job.id, 'DraftApproved')
      .catch(this._createSnackbarErrorHandler('Unable to approve the draft'))
    this._closeDraftApprovalModals()
  })

  /**
   * Whether the requested changes can be submitted.
   */
  @computed
  get canSubmitRequestedChanges() {
    return this.requestedChanges.trim().length > 0
  }

  /**
   * Sends the requested changes to the Pro and rejects the draft return.
   */
  submitRequestedChanges = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    void this._sendMessage(
      `Changes requested to the draft: ${this.requestedChanges.trim()}`,
    )

    await this._progressionStore
      .transitionToStep(this.job.id, 'DraftRejected')
      .catch(this._createSnackbarErrorHandler('Unable to request changes'))
    this._closeDraftApprovalModals()
  })

  /**
   * Cancels the request changes flow, discarding any typed changes.
   */
  @action.bound
  cancelRequestChanges() {
    this.requestedChanges = ''
    this.validation.reset()
    this.confirmDraftRejectionModalVm.close()
  }

  /**
   * Closes all draft approval related modals.
   * @private
   */
  @action.bound
  private _closeDraftApprovalModals() {
    this.requestedChanges = ''
    this.validation.reset()
    this.modal.close()
    this.confirmDraftApprovalModalVm.close()
    this.confirmDraftRejectionModalVm.close()
  }
}
